//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import axios, {
  AxiosInstance, AxiosProgressEvent, AxiosRequestConfig
} from 'axios';
import LoginRequiredException from '@/utils/LoginRequiredException';
import { readCookie } from '@/utils/cookies';
import { config } from './apiConfig';
import { type AuthUser } from './apiTypes';

export type HttpMethod = 'get' | 'post' | 'put' | 'delete';

// Header set on automated background-poll requests (data-freshness refetches,
// live job/status polls).  The backend authenticates and serves these normally
// but does not slide the inactivity timeout for them, so only genuine
// user-initiated activity keeps a session alive.  Callers opt in via
// `backgroundPollHeaders(true)`; mirrored backend-side by BACKGROUND_POLL_HEADER.
export const BACKGROUND_POLL_HEADER = 'X-Background-Poll';

export const backgroundPollHeaders = (
  background?: boolean
): Record<string, string> | null =>
  background ? { [BACKGROUND_POLL_HEADER]: '1' } : null;

export interface XgtConnectionInfo {
  host: string;
  port: number;
}

// The shared transport + session core: owns the axios instance, the session/
// auth state, and makeRequest.  Domain services depend on this (via makeRequest)
// and must share a single instance so they all read the same session token.
//
// makeRequest signals session expiry by invoking the `onSessionExpired` hook
// rather than calling logout directly — the composition root wires that hook to
// AuthService.logout, which keeps this core free of an auth-domain dependency.
export class ApiClient {
  axiosInstance: AxiosInstance;
  user: AuthUser | null;
  authType: string | null;
  userLoaded: boolean;
  xgtConnectionInfo: XgtConnectionInfo;
  restoreSessionEnabled: boolean;
  sessionExpiredTime: number | null;

  // Wired by the session-lifecycle owner; default no-op so the core works
  // standalone (e.g. in tests or before the hook is set).
  onSessionExpired: () => Promise<void> = async () => {};

  // Invoked after a successful, authenticated, non-background request.  The
  // composition root routes it to the idle-timeout controller so the client's
  // expiry clock stays in lockstep with the backend's inactivity timer (which
  // such a request just slid forward).  Default no-op for standalone use.
  onServerActivity: () => void = () => {};

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: config.MC_API_URL,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true
    });
    this.user = null;
    this.authType = null;
    this.userLoaded = false;
    this.xgtConnectionInfo = { host: 'xgt', port: 4367 };
    this.restoreSessionEnabled = true;
    this.sessionExpiredTime = null;
  }

  // The return type depends on `ignoreError`: with it false/omitted the
  // request resolves to T or throws; with it true, non-401 errors are
  // swallowed so it may resolve to undefined.
  makeRequest<T = unknown>(
    endpoint: string,
    method?: HttpMethod,
    data?: unknown,
    ignoreError?: false,
    onUploadProgress?:
      ((progressEvent: AxiosProgressEvent) => void) | null,
    additionalHeaders?: Record<string, string> | null,
    blob?: boolean,
    returnFullResponse?: boolean,
    signal?: AbortSignal | null
  ): Promise<T>;
  makeRequest<T = unknown>(
    endpoint: string,
    method: HttpMethod,
    data: unknown,
    ignoreError: true,
    onUploadProgress?:
      ((progressEvent: AxiosProgressEvent) => void) | null,
    additionalHeaders?: Record<string, string> | null,
    blob?: boolean,
    returnFullResponse?: boolean,
    signal?: AbortSignal | null
  ): Promise<T | undefined>;
  async makeRequest<T = unknown>(
    endpoint: string,
    method: HttpMethod = 'get',
    data: unknown = null,
    ignoreError = false,  // Ignores all errors besides logout ones.
    onUploadProgress:
      ((progressEvent: AxiosProgressEvent) => void) | null = null,
    additionalHeaders: Record<string, string> | null = null,
    blob = false,
    returnFullResponse = false,
    signal: AbortSignal | null = null
  ): Promise<T | undefined> {
    const cookie = readCookie('csrf_access_token');

    // Setup the payload.
    const headers: Record<string, string> = {
      'X-CSRF-TOKEN': cookie
    };

    if (additionalHeaders)
      Object.assign(headers, additionalHeaders);

    const payload: AxiosRequestConfig = {
      url: endpoint,
      method: method,
      data: data,
      headers,
      withCredentials: true // This ensures cookies are sent with requests.
    };

    if (onUploadProgress)
      payload.onUploadProgress = onUploadProgress;

    if (blob)
      payload.responseType = 'blob';

    // Set Authorization header if token is available and endpoint requires
    // authentication.  `endpoint` is the unprefixed path (axios prepends the
    // `/api/` baseURL afterward) and may carry a query string, so strip that
    // and match the pre-auth endpoints on an exact-or-path-boundary basis (not
    // a bare suffix, which would also catch e.g. `custom-login`).
    const path = endpoint.split('?')[0];
    const isPreAuth =
      path === 'login' || path.endsWith('/login') ||
      path === 'config/app' || path.endsWith('/config/app');
    if (this.user && typeof this.user === 'object' && 'token' in this.user &&
        !isPreAuth) {
      headers['Authorization'] = `Bearer ${this.user.token}`;
    }

    // Run the request.
    try {
      if (signal)
        payload.signal = signal;

      const response = await this.axiosInstance.request(payload);
      // A successful, non-background, authenticated request means the backend
      // just slid its inactivity timer forward; mirror that on the client so
      // the idle controller's expiry clock tracks the server.  Background polls
      // are excluded — they intentionally do not count as activity.
      if (!isPreAuth && !headers[BACKGROUND_POLL_HEADER] && this.user)
        this.onServerActivity();
      if (returnFullResponse)
        return response as T;
      // A body the browser could not parse comes back as the text of it, typed as
      // the answer the caller asked for: axios hands the string over rather than
      // failing.  Whatever reads it as an object then dies on the first field it
      // touches, a screen at a time and nowhere near the request -- which is how a
      // frame holding an infinity took the Datasets page down with it.  Refused
      // here instead, where a caller can say so.
      //
      // Only where it looks like an object or an array: a body that is genuinely a
      // JSON string arrives as a string too, and it is not broken.
      const body: unknown = response.data;
      if (typeof body === 'string' && /^\s*[{[]/.test(body)) {
        try {
          JSON.parse(body);
        } catch {
          throw new Error(
            'The server answered with a body that could not be read as JSON.');
        }
      }
      return response.data;
    } catch (error) {
      // Throws LoginRequiredException on a session-expired error (covers every
      // authenticated call, including auto/focus-refetch); returns otherwise.
      // `path` lets it skip the logout call itself so a 401 there can't loop.
      await this.handleSessionExpiry(error, path);
      if (!ignoreError)
        throw error;
      // ignoreError is true and the error is not session-expiry — swallow
      // silently and resolve to undefined.  The overload signatures surface
      // this as Promise<T | undefined> to callers that pass ignoreError=true
      // (e.g. restoreSession), so the undefined is type-visible, not cast away.
      return undefined;
    }
  }

  // Detect a session-expired error and, if so, run the session-expiry hook and
  // throw LoginRequiredException; return normally otherwise so callers fall
  // through to their own error handling.  Shared by makeRequest and the bespoke
  // request in PluginService.fetchPluginInfo so the detection stays in one
  // place.  A 401 is the normal expired-session signal; a 422 with msg
  // "Signature verification failed" means the backend restarted and rotated its
  // JWT secret — same recovery (re-login).  `path` (the query-stripped, /api/-
  // unprefixed endpoint) excludes the logout call itself: a 401 on logout must
  // not re-trigger onSessionExpired -> logout -> 401 -> ... forever.
  async handleSessionExpiry(error: unknown, path = ''): Promise<void> {
    const isLogout = path === 'logout' || path.endsWith('/logout');
    const sessionExpired =
      axios.isAxiosError(error) && !isLogout &&
      (error.response?.status === 401 ||
       (error.response?.status === 422 &&
        error.response?.data?.msg === 'Signature verification failed'));
    if (!sessionExpired)
      return;
    this.sessionExpiredTime = Date.now();
    // The hook (logout) may itself fail — e.g. the logout request also 401s.
    // Swallow that so we still throw LoginRequiredException, which is what
    // drives the redirect; the hook clears local state regardless.
    try {
      await this.onSessionExpired();
    } catch (err) {
      console.error('Error during session-expiry cleanup:', err);
    }
    throw new LoginRequiredException(
      'Login required to access this resource');
  }

  // --- Session state accessors ---

  isUserLoaded(): boolean {
    return this.userLoaded;
  }

  getUser(): AuthUser | null {
    return this.user;
  }

  getAuthType(): string | null {
    return this.authType;
  }

  getSessionExpiredTime(): number | null {
    return this.sessionExpiredTime;
  }

  // Method to set the user and token upon successful login.
  setUser(userData: AuthUser | null) {
    this.user = userData;
  }

  setAuthType(authType: string | null) {
    this.authType = authType;
  }

  setXGTConnection(xgtCnxInfo: XgtConnectionInfo) {
    this.xgtConnectionInfo = xgtCnxInfo;
  }

  setRestoreSessionEnabled(restoreSessionEnabled: boolean) {
    this.restoreSessionEnabled = restoreSessionEnabled;
  }

  clearSessionExpired() {
    this.sessionExpiredTime = null;
  }

  // Mark the session expired without a server round-trip, so the login page can
  // show the "session expired" notice after a client-driven idle timeout (which
  // logs out before any request would 401).
  markSessionExpired() {
    this.sessionExpiredTime = Date.now();
  }
}
